/**
 * PremiumLanding — RentNova marketing landing page.
 *
 * Implements the Claude Design "premium 3D" direction:
 *   • Three.js housing-cluster background with scroll-driven rotation
 *   • Custom cursor glow + magnetic CTAs + tilt-and-sheen cards
 *   • Cormorant Garamond italic display + Inter UI
 *   • Deep ink + warm parchment + emerald + copper palette
 *
 * All copy is i18n-driven (en / sk / de) using the existing `marketing.*` keys.
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHousingScene } from './useHousingScene';
import LanguageSwitcher from './LanguageSwitcher';
import './PremiumLanding.css';

type BillingMode = 'monthly' | 'yearly';

const PRICE_PLANS: Array<{
  id: 'free' | 'basic' | 'pro' | 'business';
  monthly: number;
  yearly: number;
  cta: string;
  highlight?: boolean;
}> = [
  { id: 'free',     monthly: 0,  yearly: 0,  cta: '/register' },
  { id: 'basic',    monthly: 9,  yearly: 7,  cta: '/register' },
  { id: 'pro',      monthly: 19, yearly: 15, cta: '/register', highlight: true },
  { id: 'business', monthly: 49, yearly: 39, cta: '/contact' },
];

export default function PremiumLanding() {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sceneState = useHousingScene(canvasRef);

  const [billing, setBilling] = useState<BillingMode>('monthly');
  const [tour, setTour] = useState<'landlord' | 'tenant'>('landlord');
  const [slide, setSlide] = useState(0);
  const [paused, setPaused] = useState(false);
  const [tourProgress, setTourProgress] = useState(0);

  const SLIDE_DURATION_MS = 5000;
  const SLIDE_COUNT = 5;

  // Reset slide when role swaps
  useEffect(() => { setSlide(0); setTourProgress(0); }, [tour]);

  // Reset progress when slide changes (manual or auto)
  useEffect(() => { setTourProgress(0); }, [slide]);

  // Auto-advance slideshow with timer-driven progress (pauses on hover).
  // Uses setInterval so it keeps running in inactive tabs (rAF gets throttled).
  useEffect(() => {
    if (paused) return;
    const TICK_MS = 80;
    const id = window.setInterval(() => {
      setTourProgress((p) => {
        const np = p + TICK_MS / SLIDE_DURATION_MS;
        if (np >= 1) {
          setSlide((s) => (s + 1) % SLIDE_COUNT);
          return 0;
        }
        return np;
      });
    }, TICK_MS);
    return () => window.clearInterval(id);
  }, [paused, tour]);

  const tintRef = useRef<HTMLDivElement | null>(null);
  const cursorRef = useRef<HTMLDivElement | null>(null);
  const billingTrackRef = useRef<HTMLDivElement | null>(null);
  const billingPillRef = useRef<HTMLSpanElement | null>(null);

  // ---- Scroll → 3D progress + tint update ----------------------------------
  useEffect(() => {
    const onScroll = () => {
      const max = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      const p = Math.min(Math.max(window.scrollY / max, 0), 1);
      sceneState.current.scrollProgress = p;
      const tint = tintRef.current;
      if (tint) {
        const cy = 30 + p * 30;
        const isPricing = p > 0.6 && p < 0.85;
        const tintColor = isPricing
          ? 'rgba(216, 238, 227, 0.35)'
          : 'rgba(245, 242, 234, 0.05)';
        tint.style.background =
          `radial-gradient(80% 60% at 50% ${cy}%, ${tintColor},` +
          ' rgba(245, 242, 234, 0.55) 60%, rgba(245, 242, 234, 0.92) 100%)';
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [sceneState]);

  // ---- Cursor glow + smoothed pointer feed for the 3D scene ----------------
  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;

    let mx = window.innerWidth / 2;
    let my = window.innerHeight / 2;
    let cx = mx;
    let cy = my;
    let raf = 0;
    let stopped = false;

    const setPos = (x: number, y: number) => {
      cursor.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
    };

    const onMove = (e: MouseEvent) => {
      mx = e.clientX;
      my = e.clientY;
      // Direct fallback in case rAF is throttled
      cx = cx + (mx - cx) * 0.5;
      cy = cy + (my - cy) * 0.5;
      setPos(cx, cy);
      sceneState.current.pointerX = (mx / window.innerWidth) * 2 - 1;
      sceneState.current.pointerY = (my / window.innerHeight) * 2 - 1;
    };
    window.addEventListener('mousemove', onMove);

    const tick = () => {
      if (stopped) return;
      cx += (mx - cx) * 0.18;
      cy += (my - cy) * 0.18;
      setPos(cx, cy);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
    };
  }, [sceneState]);

  // ---- Hover targets toggle the cursor's expanded state --------------------
  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;
    const els = document.querySelectorAll<HTMLElement>(
      '.premium-landing a, .premium-landing button, ' +
      '.premium-landing .pl-role, .premium-landing .pl-feat, ' +
      '.premium-landing .pl-step, .premium-landing .pl-price, ' +
      '.premium-landing .pl-tour-card'
    );
    const enter = () => cursor.classList.add('is-hover');
    const leave = () => cursor.classList.remove('is-hover');
    els.forEach((el) => {
      el.addEventListener('mouseenter', enter);
      el.addEventListener('mouseleave', leave);
    });
    return () => {
      els.forEach((el) => {
        el.removeEventListener('mouseenter', enter);
        el.removeEventListener('mouseleave', leave);
      });
    };
  });

  // ---- Magnetic + tilt + sheen --------------------------------------------
  useEffect(() => {
    const root = document.querySelector<HTMLElement>('.premium-landing');
    if (!root) return;

    // Magnetic
    const magnets = root.querySelectorAll<HTMLElement>('.pl-magnetic');
    const onMagMove = (btn: HTMLElement) => (e: MouseEvent) => {
      const r = btn.getBoundingClientRect();
      const x = e.clientX - r.left - r.width / 2;
      const y = e.clientY - r.top - r.height / 2;
      btn.style.transform = `translate(${x * 0.25}px, ${y * 0.25}px)`;
    };
    const onMagLeave = (btn: HTMLElement) => () => { btn.style.transform = ''; };
    const magCleanup: Array<() => void> = [];
    magnets.forEach((btn) => {
      const mv = onMagMove(btn);
      const lv = onMagLeave(btn);
      btn.addEventListener('mousemove', mv);
      btn.addEventListener('mouseleave', lv);
      magCleanup.push(() => {
        btn.removeEventListener('mousemove', mv);
        btn.removeEventListener('mouseleave', lv);
      });
    });

    // Tilt + sheen
    const tilts = root.querySelectorAll<HTMLElement>('.pl-tilt');
    const tiltCleanup: Array<() => void> = [];
    tilts.forEach((el) => {
      let rect: DOMRect | null = null;
      const enter = () => { rect = el.getBoundingClientRect(); };
      const move = (e: MouseEvent) => {
        if (!rect) rect = el.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = (e.clientY - rect.top) / rect.height;
        const rx = (0.5 - y) * 8;
        const ry = (x - 0.5) * 10;
        el.style.transform =
          `perspective(1000px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
        el.style.setProperty('--pl-mx', `${x * 100}%`);
        el.style.setProperty('--pl-my', `${y * 100}%`);
      };
      const leave = () => { el.style.transform = ''; rect = null; };
      el.addEventListener('mouseenter', enter);
      el.addEventListener('mousemove', move);
      el.addEventListener('mouseleave', leave);
      tiltCleanup.push(() => {
        el.removeEventListener('mouseenter', enter);
        el.removeEventListener('mousemove', move);
        el.removeEventListener('mouseleave', leave);
      });
    });

    return () => {
      magCleanup.forEach((f) => f());
      tiltCleanup.forEach((f) => f());
    };
  }, [tour, billing]); // re-bind when content swaps

  // ---- Reveal-on-scroll ----------------------------------------------------
  useEffect(() => {
    const root = document.querySelector<HTMLElement>('.premium-landing');
    if (!root) return;
    const els = root.querySelectorAll<HTMLElement>(
      '.pl-hero-inner > *, .pl-section-inner > *, .pl-cta-inner > *,' +
      ' .pl-tour-card, .pl-footer-inner > *'
    );
    els.forEach((el) => el.classList.add('pl-reveal'));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // ---- Billing pill repositioning -----------------------------------------
  const positionPill = useCallback((mode: BillingMode) => {
    const track = billingTrackRef.current;
    const pill = billingPillRef.current;
    if (!track || !pill) return;
    const active = track.querySelector<HTMLButtonElement>(`button[data-bill="${mode}"]`);
    if (!active) return;
    const tr = track.getBoundingClientRect();
    const ar = active.getBoundingClientRect();
    pill.style.width = `${ar.width}px`;
    pill.style.transform = `translateX(${ar.left - tr.left - 7}px)`;
  }, []);

  useEffect(() => {
    positionPill(billing);
    const onResize = () => positionPill(billing);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [billing, positionPill]);

  // ---- Smooth in-page anchor scroll ---------------------------------------
  const anchor = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    const tgt = document.getElementById(id);
    if (tgt) {
      window.scrollTo({
        top: tgt.getBoundingClientRect().top + window.scrollY - 30,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="premium-landing">
      <canvas ref={canvasRef} className="pl-bg-canvas" />
      <div ref={tintRef} className="pl-bg-tint" />
      <div className="pl-bg-vignette" />
      <div ref={cursorRef} className="pl-cursor-glow" />

      <div className="pl-content">
        {/* ============================== NAV ============================== */}
        <header className="pl-nav">
          <div className="pl-nav-inner">
            <Link to="/" className="pl-brand">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 11.5 12 4l9 7.5" />
                <path d="M5 10v10h14V10" />
                <path d="M10 20v-5h4v5" />
              </svg>
              <span>RentNova</span>
            </Link>
            <nav className="pl-nav-links">
              <a href="#features" onClick={anchor('features')}>{t('marketing.navbar.features')}</a>
              <a href="#how" onClick={anchor('how')}>{t('marketing.navbar.howItWorks')}</a>
              <a href="#pricing" onClick={anchor('pricing')}>{t('marketing.navbar.pricing')}</a>
              <Link to="/contact">{t('marketing.navbar.contact')}</Link>
            </nav>
            <div className="pl-nav-right">
              <LanguageSwitcher variant="navbar" />
              <Link to="/login" className="pl-login">{t('marketing.navbar.login')}</Link>
              <Link to="/register" className="pl-btn pl-btn-primary pl-magnetic">
                {t('marketing.navbar.startFree')}
              </Link>
            </div>
          </div>
        </header>

        {/* ============================== HERO ============================== */}
        <section className="pl-hero">
          <div className="pl-hero-inner">
            <div className="pl-eyebrow">
              <span className="pl-pulse" />
              {t('marketing.hero.badge')}
            </div>
            <h1 className="pl-display">
              {t('marketing.hero.titleLead')}<br />
              <em>{t('marketing.hero.titleHighlight')}</em>
            </h1>
            <p className="pl-lede">{t('marketing.hero.subtitle')}</p>

            <div className="pl-get-started">
              {t('marketing.hero.roleLabel').toUpperCase()}
            </div>
            <div className="pl-role-grid">
              <Link to="/register?role=landlord" className="pl-role pl-tilt">
                <div className="pl-role-head">
                  <div className="pl-role-icon pl-icon-emerald">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="1.6">
                      <rect x="3" y="3" width="7" height="7" rx="1.2" />
                      <rect x="14" y="3" width="7" height="7" rx="1.2" />
                      <rect x="3" y="14" width="7" height="7" rx="1.2" />
                      <rect x="14" y="14" width="7" height="7" rx="1.2" />
                    </svg>
                  </div>
                  <div>
                    <div className="pl-role-title">{t('marketing.hero.landlord.title')}</div>
                    <div className="pl-role-sub">{t('marketing.hero.landlord.tagline')}</div>
                  </div>
                  <span className="pl-role-arrow">→</span>
                </div>
                <ul className="pl-role-list">
                  <li><span className="pl-check">✓</span> {t('marketing.hero.landlord.f1')}</li>
                  <li><span className="pl-check">✓</span> {t('marketing.hero.landlord.f2')}</li>
                  <li><span className="pl-check">✓</span> {t('marketing.hero.landlord.f3')}</li>
                </ul>
              </Link>

              <Link to="/register?role=tenant" className="pl-role pl-tilt">
                <div className="pl-role-head">
                  <div className="pl-role-icon pl-icon-copper">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="1.6">
                      <circle cx="12" cy="8" r="3.5" />
                      <path d="M5 20c1-4 4-6 7-6s6 2 7 6" />
                    </svg>
                  </div>
                  <div>
                    <div className="pl-role-title">{t('marketing.hero.tenant.title')}</div>
                    <div className="pl-role-sub">{t('marketing.hero.tenant.tagline')}</div>
                  </div>
                  <span className="pl-role-arrow">→</span>
                </div>
                <ul className="pl-role-list">
                  <li><span className="pl-check">✓</span> {t('marketing.hero.tenant.f1')}</li>
                  <li><span className="pl-check">✓</span> {t('marketing.hero.tenant.f2')}</li>
                  <li><span className="pl-check">✓</span> {t('marketing.hero.tenant.f3')}</li>
                </ul>
              </Link>
            </div>

            <div className="pl-hero-meta">
              <span>{t('marketing.hero.note')}</span>
            </div>
          </div>
        </section>

        {/* ============================ FEATURES ============================ */}
        <section className="pl-section" id="features">
          <div className="pl-section-inner">
            <div className="pl-kicker">{t('marketing.features.label').toUpperCase()}</div>
            <h2 className="pl-display-2">
              {t('marketing.features.title').split('—')[0]}—<br />
              <em>{(t('marketing.features.title').split('—')[1] || '').trim() || 'nothing you don\'t.'}</em>
            </h2>
            <p className="pl-section-lede">{t('marketing.features.subtitle')}</p>

            <div className="pl-feat-grid">
              {[
                { key: 'property',     icon: 'emerald', svg: <><path d="M3 21V8l9-5 9 5v13" /><path d="M9 21v-7h6v7" /></> },
                { key: 'tenantPortal', icon: 'copper',  svg: <><rect x="6" y="2" width="12" height="20" rx="2" /><circle cx="12" cy="18" r="1" /></> },
                { key: 'rent',         icon: 'jade',    svg: <><rect x="3" y="6" width="18" height="14" rx="2" /><path d="M3 11h18" /><path d="M7 16h3" /></> },
                { key: 'docs',         icon: 'sand',    svg: <><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6M9 17h4" /></> },
                { key: 'chat',         icon: 'emerald', svg: <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /> },
                { key: 'maintenance',  icon: 'copper',  svg: <path d="M14.7 6.3a4 4 0 0 1-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 1 5.4-5.4z" /> },
                { key: 'tenants',      icon: 'jade',    svg: <><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 19c.6-3.4 3-5 6-5s5.4 1.6 6 5" /><path d="M15 19c.4-2.4 1.6-4 3.5-4S22 16.6 22 19" /></> },
                { key: 'finance',      icon: 'sand',    svg: <><path d="M3 3v18h18" /><path d="M7 14l4-4 3 3 5-6" /></> },
              ].map((f) => (
                <article key={f.key} className="pl-feat pl-tilt">
                  <div className={`pl-feat-icon pl-icon-${f.icon}`}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="1.6">
                      {f.svg}
                    </svg>
                  </div>
                  <h3>{t(`marketing.features.items.${f.key}.title`)}</h3>
                  <p>{t(`marketing.features.items.${f.key}.desc`)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ============================ HOW ============================ */}
        <section className="pl-section" id="how">
          <div className="pl-section-inner">
            <div className="pl-kicker">{t('marketing.howItWorks.label').toUpperCase()}</div>
            <h2 className="pl-display-2">
              {t('marketing.howItWorks.title').replace('in 3 steps', '')}<br />
              <em>in 3 steps.</em>
            </h2>
            <p className="pl-section-lede">{t('marketing.howItWorks.subtitle')}</p>

            <div className="pl-steps">
              <div className="pl-step pl-tilt">
                <div className="pl-step-num">01</div>
                <div className="pl-step-icon pl-icon-emerald">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.6">
                    <path d="M3 21V8l9-5 9 5v13" /><path d="M9 21v-7h6v7" />
                  </svg>
                </div>
                <h3>{t('marketing.howItWorks.step1.title')}</h3>
                <p>{t('marketing.howItWorks.step1.desc')}</p>
                <div className="pl-mock">
                  <div className="pl-mock-head">
                    <span>Add Property</span>
                    <span className="pl-mock-plus">+</span>
                  </div>
                  <div className="pl-mock-row"><div className="pl-mock-label">NAME</div><div className="pl-mock-val">Maple Street Apartment</div></div>
                  <div className="pl-mock-row"><div className="pl-mock-label">ADDRESS</div><div className="pl-mock-val">42 Maple St, Unit 1A</div></div>
                  <div className="pl-mock-row"><div className="pl-mock-label">MONTHLY RENT</div><div className="pl-mock-val">€1,400</div></div>
                </div>
              </div>

              <div className="pl-step pl-tilt">
                <div className="pl-step-num">02</div>
                <div className="pl-step-icon pl-icon-copper">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.6">
                    <circle cx="12" cy="8" r="3.5" />
                    <path d="M5 20c1-4 4-6 7-6s6 2 7 6" />
                  </svg>
                </div>
                <h3>{t('marketing.howItWorks.step2.title')}</h3>
                <p>{t('marketing.howItWorks.step2.desc')}</p>
                <div className="pl-mock">
                  <div className="pl-mock-head"><span>Tenants</span></div>
                  <div className="pl-mock-tenant">
                    <div className="pl-avatar pl-avatar-emerald">SM</div>
                    <div>
                      <div className="pl-t-name">Sarah Miller</div>
                      <div className="pl-t-meta">Maple St · Lease: Jan–Dec 2026</div>
                    </div>
                  </div>
                  <div className="pl-mock-tenant">
                    <div className="pl-avatar pl-avatar-copper">JC</div>
                    <div>
                      <div className="pl-t-name">James Chen</div>
                      <div className="pl-t-meta">Oak Ave · Lease: Mar–Feb 2027</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pl-step pl-tilt">
                <div className="pl-step-num">03</div>
                <div className="pl-step-icon pl-icon-jade">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.6">
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                </div>
                <h3>{t('marketing.howItWorks.step3.title')}</h3>
                <p>{t('marketing.howItWorks.step3.desc')}</p>
                <div className="pl-mock">
                  <div className="pl-mock-head"><span>Overview</span></div>
                  <div className="pl-ov-row pl-ov-good"><span>€</span><span>€4,200 collected</span><span>✓</span></div>
                  <div className="pl-ov-row pl-ov-warn"><span>!</span><span>1 overdue payment</span><span>…</span></div>
                  <div className="pl-ov-row pl-ov-info"><span>⚒</span><span>Leaky faucet — Unit 2C</span><span className="pl-ov-pill">Open</span></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================ TOUR ============================ */}
        <section className="pl-section">
          <div className="pl-section-inner">
            <div className="pl-kicker">{t('marketing.tour.label').toUpperCase()}</div>
            <h2 className="pl-display-2">
              A tour of <em>RentNova.</em>
            </h2>
            <p className="pl-section-lede">{t('marketing.tour.subtitle')}</p>

            <div className="pl-tour-toggle">
              <button
                type="button"
                className={`pl-tour-btn ${tour === 'landlord' ? 'is-active' : ''}`}
                onClick={() => setTour('landlord')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.8">
                  <path d="M3 11.5 12 4l9 7.5" />
                  <path d="M5 10v10h14V10" />
                </svg>
                {t('marketing.tour.forLandlords')}
              </button>
              <button
                type="button"
                className={`pl-tour-btn ${tour === 'tenant' ? 'is-active' : ''}`}
                onClick={() => setTour('tenant')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.8">
                  <circle cx="12" cy="8" r="3.5" />
                  <path d="M5 20c1-4 4-6 7-6s6 2 7 6" />
                </svg>
                {t('marketing.tour.forTenants')}
              </button>
            </div>

            <TourSlideshow
              tour={tour}
              slide={slide}
              setSlide={setSlide}
              setPaused={setPaused}
              progress={tourProgress}
              paused={paused}
            />
          </div>
        </section>

        {/* ============================ PRICING ============================ */}
        <section className="pl-section" id="pricing">
          <div className="pl-section-inner">
            <div className="pl-kicker">{t('marketing.pricing.label').toUpperCase()}</div>
            <h2 className="pl-display-2">
              Simple, <em>transparent</em> pricing.
            </h2>
            <p className="pl-section-lede">{t('marketing.pricing.subtitle')}</p>

            <div className="pl-billing" ref={billingTrackRef}>
              <button
                type="button"
                data-bill="monthly"
                className={`pl-bt ${billing === 'monthly' ? 'is-active' : ''}`}
                onClick={() => setBilling('monthly')}
              >
                {t('marketing.pricing.monthly')}
              </button>
              <button
                type="button"
                data-bill="yearly"
                className={`pl-bt ${billing === 'yearly' ? 'is-active' : ''}`}
                onClick={() => setBilling('yearly')}
              >
                {t('marketing.pricing.yearly')}{' '}
                <span className="pl-bt-save">−20%</span>
              </button>
              <span ref={billingPillRef} className="pl-bt-pill" />
            </div>

            <div className="pl-price-grid">
              {PRICE_PLANS.map((p) => {
                const price = billing === 'monthly' ? p.monthly : p.yearly;
                const isPro = !!p.highlight;
                const features = t(`marketing.pricing.plans.${p.id}.features`, { returnObjects: true }) as string[];
                return (
                  <article
                    key={p.id}
                    className={`pl-price pl-tilt ${isPro ? 'pl-price-pro' : ''}`}
                  >
                    {isPro && <div className="pl-popular">{t('marketing.pricing.mostPopular').toUpperCase()}</div>}
                    <div className={`pl-price-name ${isPro ? 'pl-light' : ''}`}>
                      {t(`marketing.pricing.plans.${p.id}.name`)}
                    </div>
                    <div className={`pl-price-tag ${isPro ? 'pl-light' : ''}`}>
                      {t(`marketing.pricing.plans.${p.id}.desc`)}
                    </div>
                    <div className={`pl-amount ${isPro ? 'pl-big' : ''}`}>
                      <span className={`pl-cur ${isPro ? 'pl-em' : ''}`}>€</span>
                      <span className={`pl-num ${isPro ? 'pl-em' : ''}`}>{price}</span>
                      {p.id !== 'free' && (
                        <span className={`pl-per ${isPro ? 'pl-light' : ''}`}>{t('marketing.pricing.perMonth')}</span>
                      )}
                    </div>
                    <ul className={`pl-price-list ${isPro ? 'pl-light' : ''}`}>
                      {features.map((f) => (
                        <li key={f}>
                          <span className={`pl-check ${isPro ? 'pl-em' : ''}`}>✓</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to={p.cta}
                      className={`pl-btn pl-magnetic ${isPro ? 'pl-btn-primary' : 'pl-btn-ghost'}`}
                    >
                      {t(`marketing.pricing.plans.${p.id}.cta`)} →
                    </Link>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* ============================ CTA ============================ */}
        <section className="pl-cta">
          <div className="pl-cta-inner">
            <h2 className="pl-display-2">
              {t('marketing.finalCta.title').split('property')[0]}<br />
              <em>property management.</em>
            </h2>
            <p className="pl-section-lede">{t('marketing.finalCta.subtitle')}</p>
            <Link to="/register" className="pl-btn pl-btn-primary pl-btn-xl pl-magnetic">
              {t('marketing.finalCta.cta')} →
            </Link>
            <div className="pl-cta-meta">{t('marketing.finalCta.note')}</div>
          </div>
        </section>

        {/* ============================ FOOTER ============================ */}
        <footer className="pl-footer" id="contact">
          <div className="pl-footer-inner">
            <div className="pl-foot-brand">
              <Link to="/" className="pl-brand">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.6"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 11.5 12 4l9 7.5" />
                  <path d="M5 10v10h14V10" />
                  <path d="M10 20v-5h4v5" />
                </svg>
                <span>RentNova</span>
              </Link>
              <p>{t('footer.tagline')}</p>
              <a className="pl-foot-mail" href="mailto:contact@rentnova.app">
                ✉ contact@rentnova.app
              </a>
            </div>
            <div className="pl-foot-col">
              <div className="pl-foot-h">{t('footer.product').toUpperCase()}</div>
              <a href="#features" onClick={anchor('features')}>{t('footer.features')}</a>
              <a href="#pricing" onClick={anchor('pricing')}>{t('footer.pricing')}</a>
              <a href="#features" onClick={anchor('features')}>{t('footer.integrations')}</a>
              <a href="#features" onClick={anchor('features')}>{t('footer.changelog')}</a>
            </div>
            <div className="pl-foot-col">
              <div className="pl-foot-h">{t('footer.company').toUpperCase()}</div>
              <Link to="/contact">{t('footer.about')}</Link>
              <Link to="/contact">{t('footer.blog')}</Link>
              <Link to="/contact">{t('footer.careers')}</Link>
              <Link to="/contact">{t('footer.contactUs')}</Link>
            </div>
            <div className="pl-foot-col">
              <div className="pl-foot-h">{t('footer.legal').toUpperCase()}</div>
              <Link to="/privacy">{t('footer.privacy')}</Link>
              <Link to="/terms">{t('footer.terms')}</Link>
              <Link to="/cookies">{t('footer.cookies')}</Link>
              <Link to="/imprint">Imprint</Link>
            </div>
          </div>
          <div className="pl-foot-bottom">{t('footer.copyright')} · Crafted with care</div>
        </footer>
      </div>
    </div>
  );
}

/* ============================================================================
 * TourSlideshow — auto-advancing slideshow showcasing the actual app.
 * Each slide is a faithful mock of one screen in the dashboard / tenant portal.
 * ========================================================================== */

type TourRole = 'landlord' | 'tenant';

interface TourSlideshowProps {
  tour: TourRole;
  slide: number;
  setSlide: (n: number) => void;
  setPaused: (b: boolean) => void;
  progress: number;
  paused: boolean;
}

interface SlideMeta {
  page: 'Dashboard' | 'Properties' | 'Tenants' | 'Payments' | 'Documents' | 'Maintenance' | 'Chat';
  title: string;
  desc: string;
  iconPath: React.ReactNode;
}

const LANDLORD_SLIDES: SlideMeta[] = [
  {
    page: 'Dashboard',
    title: 'Your portfolio at a glance',
    desc: 'See every property, tenant and payment in one calm overview — with live stats and a 6-month revenue trend.',
    iconPath: (<><rect x="3" y="3" width="7" height="9" rx="1.2" /><rect x="14" y="3" width="7" height="5" rx="1.2" /><rect x="14" y="12" width="7" height="9" rx="1.2" /><rect x="3" y="16" width="7" height="5" rx="1.2" /></>),
  },
  {
    page: 'Properties',
    title: 'Every property, organized',
    desc: 'Add a property in seconds — name, address, monthly rent, due day. Edit anytime, archive when leases end.',
    iconPath: (<><path d="M3 21V8l9-5 9 5v13" /><path d="M9 21v-7h6v7" /></>),
  },
  {
    page: 'Tenants',
    title: 'Know every tenant',
    desc: 'Profiles, lease dates, contact info, invitation status — and one-click invites by email or shareable code.',
    iconPath: (<><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 19c.6-3.4 3-5 6-5s5.4 1.6 6 5" /><path d="M15 19c.4-2.4 1.6-4 3.5-4S22 16.6 22 19" /></>),
  },
  {
    page: 'Payments',
    title: 'Track every euro',
    desc: 'Mark rent paid in one click. Auto-detect overdue. Color-coded status across all your units, all months.',
    iconPath: (<><rect x="3" y="6" width="18" height="14" rx="2" /><path d="M3 11h18" /><path d="M7 16h3" /></>),
  },
  {
    page: 'Documents',
    title: 'Digital documents & e-signing',
    desc: 'Upload leases, send for e-signature, store the signed copy — no printing, no scanning.',
    iconPath: (<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /><path d="M9 13h6M9 17h4" /></>),
  },
];

const TENANT_SLIDES: SlideMeta[] = [
  {
    page: 'Dashboard',
    title: 'Your home, simplified',
    desc: 'See your next payment, lease term, and quick actions the moment you log in.',
    iconPath: (<><path d="M3 11.5 12 4l9 7.5" /><path d="M5 10v10h14V10" /></>),
  },
  {
    page: 'Payments',
    title: 'Pay rent in seconds',
    desc: 'See what\'s due, when, and the full payment history — with secure card or bank transfer.',
    iconPath: (<><rect x="3" y="6" width="18" height="14" rx="2" /><path d="M3 11h18" /><path d="M7 16h3" /></>),
  },
  {
    page: 'Maintenance',
    title: 'Report issues fast',
    desc: 'Snap a photo, describe the issue, watch your landlord take action — all in real time.',
    iconPath: (<path d="M14.7 6.3a4 4 0 0 1-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 1 5.4-5.4z" />),
  },
  {
    page: 'Documents',
    title: 'Sign in one click',
    desc: 'Lease agreements arrive in your portal. Sign on screen with your finger or mouse — done.',
    iconPath: (<><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" /></>),
  },
  {
    page: 'Chat',
    title: 'Reach your landlord',
    desc: 'Direct, threaded messages — no more waiting on emails or phone tag.',
    iconPath: (<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />),
  },
];

function TourSlideshow({ tour, slide, setSlide, setPaused, progress, paused }: TourSlideshowProps) {
  const slides = tour === 'landlord' ? LANDLORD_SLIDES : TENANT_SLIDES;
  const meta = slides[slide];

  return (
    <div
      className="pl-tour-card pl-tilt"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="pl-tour-left">
        <div className="pl-tour-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.6">
            {meta.iconPath}
          </svg>
        </div>
        <h3>{meta.title}</h3>
        <p>{meta.desc}</p>
        <div className="pl-tour-nav">
          <div className="pl-dots" role="tablist" aria-label="Tour slides">
            {slides.map((s, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === slide}
                aria-label={`Go to slide ${i + 1}: ${s.page}`}
                className={`pl-dot ${i === slide ? 'is-active' : ''} ${i < slide ? 'is-past' : ''}`}
                onClick={() => setSlide(i)}
              />
            ))}
            <span className="pl-dots-counter">{slide + 1} / {slides.length}</span>
          </div>
          <div
            className="pl-lane"
            role="progressbar"
            aria-label="Time until next slide"
            aria-valuenow={Math.round(progress * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className={`pl-lane__fill${paused ? ' is-paused' : ''}`}
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        </div>
      </div>
      <div className="pl-tour-right">
        <div className="pl-window">
          <div className="pl-window-bar">
            <span className="pl-mac pl-mac-red" />
            <span className="pl-mac pl-mac-yellow" />
            <span className="pl-mac pl-mac-green" />
            <span className="pl-window-url">
              app.rentnova.com/{tour}/{meta.page.toLowerCase()}
            </span>
          </div>
          <div className="pl-window-body">
            <AppSidebar tour={tour} active={meta.page} />
            <main className="pl-main">
              <SlideContent tour={tour} page={meta.page} />
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}

function AppSidebar({ tour, active }: { tour: TourRole; active: SlideMeta['page'] }) {
  const items: Array<SlideMeta['page']> = tour === 'landlord'
    ? ['Dashboard', 'Properties', 'Tenants', 'Payments', 'Documents', 'Maintenance']
    : ['Dashboard', 'Payments', 'Maintenance', 'Documents', 'Chat'];

  return (
    <aside className="pl-side">
      <div className="pl-side-brand">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.8">
          <path d="M3 11.5 12 4l9 7.5" />
          <path d="M5 10v10h14V10" />
        </svg>
        RentNova
      </div>
      {items.map((it) => (
        <div key={it} className={`pl-side-item ${active === it ? 'is-active' : ''}`}>
          {it}
        </div>
      ))}
    </aside>
  );
}

/* --- The actual screen mocks ------------------------------------------------ */

function SlideContent({ tour, page }: { tour: TourRole; page: SlideMeta['page'] }) {
  if (tour === 'landlord') {
    if (page === 'Dashboard')   return <LandlordDashboardMock />;
    if (page === 'Properties')  return <PropertiesMock />;
    if (page === 'Tenants')     return <TenantsMock />;
    if (page === 'Payments')    return <PaymentsMock />;
    if (page === 'Documents')   return <DocumentsMock />;
  } else {
    if (page === 'Dashboard')   return <TenantDashboardMock />;
    if (page === 'Payments')    return <TenantPaymentsMock />;
    if (page === 'Maintenance') return <TenantMaintenanceMock />;
    if (page === 'Documents')   return <TenantDocumentsMock />;
    if (page === 'Chat')        return <TenantChatMock />;
  }
  return null;
}

function LandlordDashboardMock() {
  return (
    <div className="pl-mock-page">
      <div className="pl-main-head">
        <span className="pl-main-title">Dashboard</span>
        <span className="pl-tag">Today · April 26</span>
      </div>
      <div className="pl-kpi-grid">
        <div className="pl-kpi"><div className="pl-kpi-label">Properties</div><div className="pl-kpi-val">3</div></div>
        <div className="pl-kpi"><div className="pl-kpi-label">Tenants</div><div className="pl-kpi-val">5</div></div>
        <div className="pl-kpi"><div className="pl-kpi-label">Collected</div><div className="pl-kpi-val">€4,200</div></div>
        <div className="pl-kpi"><div className="pl-kpi-label">Open</div><div className="pl-kpi-val">1</div></div>
      </div>
      <div className="pl-rev-card">
        <div className="pl-rev-head">
          <span className="pl-rev-title">Revenue · 6 mo</span>
          <span className="pl-rev-trend">↑ 12%</span>
        </div>
        <svg viewBox="0 0 280 56" className="pl-rev-svg" preserveAspectRatio="none">
          <defs>
            <linearGradient id="pl-rev-g" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#0E7C5A" stopOpacity="0.35" />
              <stop offset="100%" stopColor="#0E7C5A" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d="M0,40 C40,38 60,30 90,28 S150,10 180,16 S240,24 280,12 L280,56 L0,56 Z" fill="url(#pl-rev-g)" />
          <path d="M0,40 C40,38 60,30 90,28 S150,10 180,16 S240,24 280,12" fill="none" stroke="#0E7C5A" strokeWidth="1.5" />
          <circle cx="280" cy="12" r="2.5" fill="#0E7C5A" />
        </svg>
        <div className="pl-rev-labels">
          <span>Nov</span><span>Dec</span><span>Jan</span><span>Feb</span><span>Mar</span><span>Apr</span>
        </div>
      </div>
    </div>
  );
}

function PropertiesMock() {
  return (
    <div className="pl-mock-page">
      <div className="pl-main-head">
        <span className="pl-main-title">Properties</span>
        <button type="button" className="pl-upload-btn">+ Add property</button>
      </div>
      <div className="pl-prop-grid">
        {[
          { name: 'Maple Street Apartment', addr: '42 Maple St, Unit 1A', rent: '€1,400', units: '1 unit' },
          { name: 'Oak Avenue Loft',         addr: '108 Oak Ave',         rent: '€1,650', units: '1 unit' },
          { name: 'Pine View Townhouse',     addr: '7 Pine View Ln',      rent: '€2,100', units: '2 units' },
        ].map((p) => (
          <div key={p.name} className="pl-prop-card">
            <div className="pl-prop-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M3 11.5 12 4l9 7.5" /><path d="M5 10v10h14V10" />
              </svg>
            </div>
            <div className="pl-prop-name">{p.name}</div>
            <div className="pl-prop-addr">{p.addr}</div>
            <div className="pl-prop-foot">
              <span className="pl-prop-rent">{p.rent}<span>/mo</span></span>
              <span className="pl-prop-units">{p.units}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TenantsMock() {
  return (
    <div className="pl-mock-page">
      <div className="pl-main-head">
        <span className="pl-main-title">Tenants</span>
        <button type="button" className="pl-upload-btn">+ Invite</button>
      </div>
      <div className="pl-tn-list">
        {[
          { ini: 'SM', name: 'Sarah Miller', meta: 'Maple St · Lease Jan–Dec 26', tag: 'Active', tone: 'good' },
          { ini: 'JC', name: 'James Chen',   meta: 'Oak Ave · Lease Mar–Feb 27',  tag: 'Active', tone: 'good' },
          { ini: 'ML', name: 'Maria López',  meta: 'Pine View · invited 2d ago',  tag: 'Invited', tone: 'warn' },
          { ini: 'AP', name: 'Adam Petrov',  meta: 'Pine View · Lease Jun–May 27', tag: 'Active', tone: 'good' },
        ].map((t) => (
          <div key={t.name} className="pl-tn-row">
            <div className={`pl-avatar pl-avatar-${t.ini[0] === 'M' || t.ini[0] === 'A' ? 'copper' : 'emerald'}`}>{t.ini}</div>
            <div className="pl-tn-info">
              <div className="pl-tn-name">{t.name}</div>
              <div className="pl-tn-meta">{t.meta}</div>
            </div>
            <span className={`pl-status pl-status-${t.tone === 'good' ? 'signed' : 'sent'}`}>{t.tag}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentsMock() {
  return (
    <div className="pl-mock-page">
      <div className="pl-main-head">
        <span className="pl-main-title">Payments</span>
        <span className="pl-tag">April 2026</span>
      </div>
      <div className="pl-doc-table">
        <div className="pl-doc-row is-head"><span>TENANT</span><span>AMOUNT</span><span>STATUS</span></div>
        <div className="pl-doc-row"><span>Sarah Miller · Maple St</span><span>€1,400</span><span className="pl-status pl-status-signed">● Paid</span></div>
        <div className="pl-doc-row"><span>James Chen · Oak Ave</span><span>€1,650</span><span className="pl-status pl-status-signed">● Paid</span></div>
        <div className="pl-doc-row"><span>Maria López · Pine View</span><span>€1,050</span><span className="pl-status pl-status-sent">Pending</span></div>
        <div className="pl-doc-row"><span>Adam Petrov · Pine View</span><span>€1,050</span><span className="pl-status pl-status-overdue">● 3d Overdue</span></div>
      </div>
    </div>
  );
}

function DocumentsMock() {
  return (
    <div className="pl-mock-page">
      <div className="pl-main-head">
        <span className="pl-main-title">Documents</span>
        <button type="button" className="pl-upload-btn">+ Upload</button>
      </div>
      <div className="pl-doc-table">
        <div className="pl-doc-row is-head"><span>DOCUMENT</span><span>TENANT</span><span>STATUS</span></div>
        <div className="pl-doc-row"><span>Lease — Maple St</span><span>Sarah M.</span><span className="pl-status pl-status-signed">● Signed</span></div>
        <div className="pl-doc-row"><span>Lease — Oak Ave</span><span>James C.</span><span className="pl-status pl-status-sent">Sent</span></div>
        <div className="pl-doc-row"><span>Move-in form</span><span>Maria L.</span><span className="pl-status pl-status-draft">Draft</span></div>
        <div className="pl-doc-row"><span>Renewal — Pine View</span><span>Adam P.</span><span className="pl-status pl-status-sent">Sent</span></div>
      </div>
    </div>
  );
}

function TenantDashboardMock() {
  return (
    <div className="pl-mock-page">
      <div className="pl-main-head">
        <span className="pl-main-title">Welcome, Sarah</span>
        <span className="pl-tag">Maple Street · 1A</span>
      </div>
      <div className="pl-due-card">
        <div className="pl-due-label">Next payment due</div>
        <div className="pl-due-amt">€1,400</div>
        <div className="pl-due-meta">May 1 · in 5 days</div>
        <button type="button" className="pl-pay-btn">Pay rent →</button>
      </div>
      <div className="pl-kpi-grid pl-kpi-grid--2">
        <div className="pl-kpi"><div className="pl-kpi-label">Lease ends</div><div className="pl-kpi-val">Dec 31</div></div>
        <div className="pl-kpi"><div className="pl-kpi-label">Open tickets</div><div className="pl-kpi-val">0</div></div>
      </div>
    </div>
  );
}

function TenantPaymentsMock() {
  return (
    <div className="pl-mock-page">
      <div className="pl-main-head">
        <span className="pl-main-title">Payments</span>
        <span className="pl-tag">€16,800 lifetime</span>
      </div>
      <div className="pl-doc-table">
        <div className="pl-doc-row is-head"><span>MONTH</span><span>AMOUNT</span><span>STATUS</span></div>
        <div className="pl-doc-row"><span>May 2026</span><span>€1,400</span><span className="pl-status pl-status-sent">Due May 1</span></div>
        <div className="pl-doc-row"><span>April 2026</span><span>€1,400</span><span className="pl-status pl-status-signed">● Paid</span></div>
        <div className="pl-doc-row"><span>March 2026</span><span>€1,400</span><span className="pl-status pl-status-signed">● Paid</span></div>
        <div className="pl-doc-row"><span>February 2026</span><span>€1,400</span><span className="pl-status pl-status-signed">● Paid</span></div>
      </div>
    </div>
  );
}

function TenantMaintenanceMock() {
  return (
    <div className="pl-mock-page">
      <div className="pl-main-head">
        <span className="pl-main-title">Maintenance</span>
        <button type="button" className="pl-upload-btn">+ New ticket</button>
      </div>
      <div className="pl-tk-list">
        <div className="pl-tk-row">
          <div className="pl-tk-icon pl-icon-emerald">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14.7 6.3a4 4 0 0 1-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 1 5.4-5.4z" />
            </svg>
          </div>
          <div className="pl-tk-info">
            <div className="pl-tk-title">Leaky kitchen faucet</div>
            <div className="pl-tk-meta">Submitted 2 days ago</div>
          </div>
          <span className="pl-status pl-status-sent">In progress</span>
        </div>
        <div className="pl-tk-row">
          <div className="pl-tk-icon pl-icon-copper">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14.7 6.3a4 4 0 0 1-5.4 5.4L4 17v3h3l5.3-5.3a4 4 0 0 1 5.4-5.4z" />
            </svg>
          </div>
          <div className="pl-tk-info">
            <div className="pl-tk-title">Bedroom window stuck</div>
            <div className="pl-tk-meta">Submitted 1 wk ago</div>
          </div>
          <span className="pl-status pl-status-signed">● Resolved</span>
        </div>
      </div>
    </div>
  );
}

function TenantDocumentsMock() {
  return (
    <div className="pl-mock-page">
      <div className="pl-main-head">
        <span className="pl-main-title">Documents</span>
        <span className="pl-tag">1 awaiting signature</span>
      </div>
      <div className="pl-doc-table">
        <div className="pl-doc-row is-head"><span>DOCUMENT</span><span>FROM</span><span>ACTION</span></div>
        <div className="pl-doc-row"><span>Lease 2026 — Maple St</span><span>You</span><span className="pl-status pl-status-signed">● Signed</span></div>
        <div className="pl-doc-row"><span>Renewal addendum</span><span>Landlord</span><span className="pl-status pl-status-sent">Sign now →</span></div>
        <div className="pl-doc-row"><span>House rules</span><span>Landlord</span><span className="pl-status pl-status-draft">Read</span></div>
      </div>
    </div>
  );
}

function TenantChatMock() {
  return (
    <div className="pl-mock-page">
      <div className="pl-main-head">
        <span className="pl-main-title">Chat with landlord</span>
        <span className="pl-tag">● Online</span>
      </div>
      <div className="pl-chat">
        <div className="pl-msg pl-msg-them">Hi Sarah — the plumber is booked for tomorrow 9am. OK?</div>
        <div className="pl-msg pl-msg-me">Perfect, I'll be home. Thanks!</div>
        <div className="pl-msg pl-msg-them">No problem 👍</div>
        <div className="pl-msg pl-msg-me">Quick Q — can I install a wall shelf in the hallway?</div>
        <div className="pl-msg pl-msg-them">Sure, just keep the holes small 🙂</div>
      </div>
      <div className="pl-chat-input">
        <span>Type a message…</span>
        <span className="pl-chat-send">↑</span>
      </div>
    </div>
  );
}
